import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { gmbApi } from '@/services/gmbApi';

function getUserId() {
  return localStorage.getItem('user_id') || '';
}

export interface ConnectionData {
  is_connected: boolean;
  setup_status: string;
  verification_status: string;
  business_name: string | null;
  google_account_email: string | null;
  last_synced_at: string | null;
  last_synced_label: string | null;
  has_no_gbp_profile?: boolean;
  mandatory_setup_completed?: boolean;
  location_choice_needed?: boolean;
}

export interface GmbProfileSetup {
  gmb_profile_url: string;
  business_name: string;
  business_description: string;
}

export function useGmbConnection(onConnected?: () => void) {
  const userId = getUserId();
  const queryClient = useQueryClient();
  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;

  const [isChecking, setIsChecking] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [isSkipped, setIsSkipped] = useState(() => {
    return localStorage.getItem('gmb_setup_skipped') === 'true';
  });
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [connectionData, setConnectionData] = useState<ConnectionData | null>(null);
  const [profileSetup, setProfileSetup] = useState<GmbProfileSetup>({
    gmb_profile_url: '',
    business_name: '',
    business_description: '',
  });
  const [error, setError] = useState<string | null>(null);
  const syncPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (syncPollRef.current) {
        clearInterval(syncPollRef.current);
      }
    };
  }, []);

  const checkConnection = useCallback(async () => {
    if (!userId) {
      setIsChecking(false);
      return;
    }

    setIsChecking(true);
    setError(null);
    try {
      const response = await gmbApi.getConnectionStatus(userId) as any;
      const data = response?.data || response;
      setConnectionData(data);
      setIsConnected(data?.is_connected === true);
    } catch (err: any) {
      setError(err.message || 'Failed to check GMB connection.');
      setIsConnected(false);
      setIsChecking(false);
      return;
    }

    try {
      const setupResponse = await gmbApi.getProfileSetup(userId) as any;
      const setupData = setupResponse?.data || setupResponse;
      setProfileSetup({
        gmb_profile_url: setupData?.gmb_profile_url || '',
        business_name: setupData?.business_name || '',
        business_description: setupData?.business_description || '',
      });
    } catch (err: any) {
      setProfileSetup({
        gmb_profile_url: '',
        business_name: '',
        business_description: '',
      });
      setError(err.message || 'Failed to load saved GMB profile setup.');
    } finally {
      setIsChecking(false);
    }
  }, [userId]);

  useEffect(() => {
    checkConnection();
  }, [checkConnection]);

  const handleOAuthLogin = useCallback(async (setup?: GmbProfileSetup) => {
    if (!userId) {
      setError('User ID not found.');
      return;
    }

    try {
      setIsOAuthLoading(true);
      setError(null);

      if (setup) {
        await gmbApi.updateProfileSetup(userId, setup);
        setProfileSetup(setup);
      }

      const response = await gmbApi.initiateOAuth(userId) as any;
      const authUrl = response?.auth_url || response?.data?.auth_url;

      if (!authUrl) {
        throw new Error('Failed to get OAuth URL.');
      }

      const width = 520;
      const height = 640;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(
        authUrl,
        'Google OAuth',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        setIsOAuthLoading(false);
        setError('Popup blocked. Allow popups for this site and try again.');
        return;
      }

      const handleMessage = async (event: MessageEvent) => {
        const apiOrigin = new URL(
          import.meta.env.VITE_API_BASE_URL || window.location.origin,
          window.location.origin
        ).origin;
        if (
          event.source !== popup ||
          event.origin !== apiOrigin
        ) {
          return;
        }

        if (event.data?.type === 'OAUTH_SUCCESS') {
          if (event.data.userId !== userId) return;
          popup?.close();
          window.removeEventListener('message', handleMessage);
          setIsOAuthLoading(false);

          localStorage.removeItem('gmb_setup_skipped');
          setIsSkipped(false);

          await checkConnection();

          queryClient.invalidateQueries({ queryKey: ['gmb'] });

          onConnectedRef.current?.();
        }

        if (event.data?.type === 'OAUTH_ERROR') {
          if (event.data.userId && event.data.userId !== userId) return;
          popup?.close();
          window.removeEventListener('message', handleMessage);
          setIsOAuthLoading(false);
          setError(event.data.error || 'Google authentication failed.');
        }
      };

      window.addEventListener('message', handleMessage);

      const timeout = setTimeout(() => {
        window.removeEventListener('message', handleMessage);
        setIsOAuthLoading(false);
      }, 5 * 60 * 1000);

      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          clearTimeout(timeout);
          window.removeEventListener('message', handleMessage);
          setIsOAuthLoading(false);
        }
      }, 500);
    } catch (err: any) {
      setIsOAuthLoading(false);
      setError(err.message || 'OAuth initialization failed.');
    }
  }, [userId, checkConnection, queryClient]);

  const handleSkip = useCallback(() => {
    localStorage.setItem('gmb_setup_skipped', 'true');
    setIsSkipped(true);
  }, []);

  const handleConnectFromLocked = useCallback(() => {
    localStorage.removeItem('gmb_setup_skipped');
    setIsSkipped(false);
  }, []);

  const handleSync = useCallback(async () => {
    if (!userId) return;

    try {
      setIsSyncing(true);
      setError(null);
      toast.info('Syncing GMB data...');
      await gmbApi.triggerSync(userId);

      const initial = await gmbApi.getConnectionStatus(userId) as any;
      const initialData = initial?.data || initial;
      const baselineLastSynced = initialData?.last_synced_at || null;
      let attempts = 0;
      const MAX_ATTEMPTS = 18; // ~3 minutes at 10s intervals

      if (syncPollRef.current) {
        clearInterval(syncPollRef.current);
      }

      syncPollRef.current = setInterval(async () => {
        attempts += 1;
        try {
          const response = await gmbApi.getConnectionStatus(userId) as any;
          const data = response?.data || response;
          setConnectionData(data);
          setIsConnected(data?.is_connected === true);

          const synced = data?.last_synced_at || null;
          if (synced && synced !== baselineLastSynced) {
            if (syncPollRef.current) {
              clearInterval(syncPollRef.current);
              syncPollRef.current = null;
            }
            setIsSyncing(false);
            toast.success('GMB data synced');
            queryClient.invalidateQueries({ queryKey: ['gmb'] });
            return;
          }

          if (attempts >= MAX_ATTEMPTS) {
            if (syncPollRef.current) {
              clearInterval(syncPollRef.current);
              syncPollRef.current = null;
            }
            setIsSyncing(false);
            toast.info('GMB sync did not complete in time. This may be due to Google API rate limits.');
            queryClient.invalidateQueries({ queryKey: ['gmb'] });
          }
        } catch (err: any) {
          if (attempts >= MAX_ATTEMPTS) {
            if (syncPollRef.current) {
              clearInterval(syncPollRef.current);
              syncPollRef.current = null;
            }
            setIsSyncing(false);
            setError(err.message || 'Sync failed.');
            toast.error(err.message || 'Sync failed.');
          }
        }
      }, 10 * 1000);
    } catch (err: any) {
      setIsSyncing(false);
      const msg = err?.rateLimited
        ? err.message || 'Rate limited by Google. Try again in a few minutes.'
        : err.message || 'Sync failed.';
      setError(msg);
      toast.error(msg);
    }
  }, [userId, queryClient]);

  const handleDisconnect = useCallback(async () => {
    if (!userId) return;

    try {
      await gmbApi.revokeOAuth(userId);
      localStorage.removeItem('gmb_setup_skipped');
      setIsSkipped(false);
      setIsConnected(false);
      setConnectionData(null);
      queryClient.invalidateQueries({ queryKey: ['gmb'] });
    } catch (err: any) {
      setError(err.message || 'Disconnect failed.');
    }
  }, [userId, queryClient]);

  // BRD "No GMB Profile" edge case (L1096-1109) — OAuth succeeded but no GBP
  // location was found under the connected account. Distinct from showGate
  // (not connected yet) and showDashboard (connected with a real profile).
  const hasNoGbpProfile = connectionData?.has_no_gbp_profile === true;
  // D-194/D-195 — mandatory full-screen setup step (business name/
  // description validation + Required/Conditional fields), shown after
  // OAuth connects and before dashboard access, persisted so it survives
  // a reload/browser-close mid-screen (resumability requirement) — derived
  // from the backend flag on every connection check, not local-only state.
  const mandatorySetupCompleted = connectionData?.mandatory_setup_completed === true;

  // D-198: several Business Profiles and the profile URL matched none.
  const locationChoiceNeeded = connectionData?.location_choice_needed === true;

  const showGate = !isConnected && !isSkipped;
  const showLocked = isSkipped && (!isConnected || hasNoGbpProfile);
  const showNoProfile = isConnected && hasNoGbpProfile && !isSkipped;
  const showLocationPicker = isConnected && locationChoiceNeeded && !isSkipped;
  const showSetupScreen = isConnected && !hasNoGbpProfile && !locationChoiceNeeded && !mandatorySetupCompleted && !isSkipped;
  const showDashboard = isConnected && !hasNoGbpProfile && !locationChoiceNeeded && mandatorySetupCompleted && !isSkipped;

  return {
    isChecking,
    isConnected,
    isSkipped,
    isOAuthLoading,
    isSyncing,
    connectionData,
    profileSetup,
    error,
    showGate,
    showLocked,
    showNoProfile,
    showLocationPicker,
    showSetupScreen,
    showDashboard,
    checkConnection,
    handleOAuthLogin,
    handleSkip,
    handleConnectFromLocked,
    handleSync,
    handleDisconnect,
    setError,
  };
}
